import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { FromFileQuoteInput, FromFileQuoteResult } from '@efacture/web-billing/billing-host/types';
import { salesKeys, type CreateQuoteInput, type Quote } from './sales';

// The quote output of a draft proposal (#241): a suite document. Online
// only, as every draft-proposal write: a queued replay could not be told
// apart from a second validation.

/** POST /api/draft-proposals/:id/quote — the quote create body. */
export function useCreateQuoteFromProposal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      proposalId,
      input,
      idempotencyKey,
    }: {
      proposalId: string;
      input: CreateQuoteInput;
      idempotencyKey: string;
    }) =>
      apiClient
        .post<{ quote: Quote }>(`/draft-proposals/${proposalId}/quote`, input, { noQueue: true, idempotencyKey })
        .then((data) => data.quote),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: salesKeys.quotes() });
    },
  });
}

/**
 * The quote output of « Facture depuis un fichier », as the billing host's
 * slot: the reviewed lines become the quote's, and the quote opens.
 */
export function useFromFileQuote(): (input: FromFileQuoteInput) => Promise<FromFileQuoteResult> {
  const quoteMutation = useCreateQuoteFromProposal();
  return async ({ proposalId, idempotencyKey, lines, ...client }) => {
    const input: CreateQuoteInput = {
      ...client,
      lines: lines.map(({ articleId, articleCode, articleName, articleType, taxGroup, quantity, unitPrice }) => ({
        articleId,
        articleCode,
        articleName,
        articleType,
        taxGroup,
        quantity,
        unitPrice,
      })),
    };
    const quote = await quoteMutation.mutateAsync({ proposalId, input, idempotencyKey });
    return { path: `/ventes/devis/${quote.id}`, message: 'Devis créé' };
  };
}
